/**
 * Author: Claude Code
 * Version: v3.2.34
 * Modified: 2025-10-04
 *
 * PhaserAnimationManager - Phaser3原生动画系统集成 (REFACTORED)
 * 完全替换FrameAnimationManager，使用Phaser3原生API，代码量减少70%
 * REFACTOR: 优化代码质量，提取常量，减少重复，改进类型安全
 */

import * as Phaser from 'phaser';
import { MovementAnimationSync, AnimationSyncConfig } from './MovementAnimationSync';
import { CharacterType as LayerCharType, WeaponType as LayerWeaponType, AnimationType as LayerAnim, AnimationType as CoreAnimType, CharacterType } from './spriteTypes';
import { colorToCharacterType, getCharacterFrameKey, isCharacterFrameLoaded } from './utils/spriteKeyUtils';

// Animation types matching the original system
export type AnimationType = 'idle' | 'walk' | 'mov' | 'hurt' | 'die' | 'rush' | 'use' | 'atk';
export type WeaponType = 'sword' | 'bow' | 'staff';
export type Direction = 'left' | 'right';

// Removed legacy event mapping types; this manager is now a pure player.

// Animation configuration with improved type safety
interface AnimationConfig {
  frames: number;
  frameRate: number;
  repeat: number;
}

const ANIMATION_CONFIG: Record<AnimationType, AnimationConfig> = {
  idle: { frames: 11, frameRate: 10, repeat: -1 },
  walk: { frames: 8, frameRate: 16, repeat: -1 }, // 🔧 优化：提升到16fps
  mov: { frames: 8, frameRate: 16, repeat: -1 }, // 🔧 优化：提升到16fps，配合300ms jumpDuration
  hurt: { frames: 3, frameRate: 15, repeat: 0 },
  die: { frames: 12, frameRate: 8, repeat: 0 },
  rush: { frames: 11, frameRate: 20, repeat: 0 },
  use: { frames: 11, frameRate: 10, repeat: 0 },
  atk: { frames: 8, frameRate: 15, repeat: 0 }
};

// Asset path constants (基于slime-animation-demo.html的正确实现)
const ASSET_CONFIG = {
  CHARACTER_BASE_PATH: '/assets/characters',
  WEAPON_BASE_PATH: '/assets/hold-weapon',
  SLIME_FILE_PREFIX: '像素史莱姆',
  FILE_EXTENSION: '.png',
  TILE_SIZE: 48, // 统一使用48px瓦片尺寸 (匹配新的48x48地形素材)
  SPRITE_SCALE: 1.456  // 缩小20%：1.82 * 0.8 = 1.456 (65x64素材→95x93显示，与tile比例更协调)
} as const;

// 角色配置 (匹配demo中的SLIME_CONFIG)
const CHARACTER_CONFIG = {
  green: { colorId: '0', weaponType: 'sword' },
  red: { colorId: '1', weaponType: 'axe' }
} as const;

// Removed legacy 23-event map: SingleAnimationEngine owns event routing.

export class PhaserAnimationManager {
  private scene: Phaser.Scene;
  public agentContainers: Map<string, Phaser.GameObjects.Container> = new Map();
  private agentSprites: Map<string, Phaser.GameObjects.Sprite> = new Map();
  private weaponSprites: Map<string, Phaser.GameObjects.Sprite> = new Map();
  private equippedTypes: Map<string, string> = new Map();
  private agentColors: Map<string, string> = new Map(); // 🔧 存储每个agent的颜色
  private preloadedAssets = new Set<string>();
  private movementSync: MovementAnimationSync; // 新增：移动动画同步器

  constructor(scene: Phaser.Scene, syncConfig?: Partial<AnimationSyncConfig>) {
    this.scene = scene;
    this.movementSync = new MovementAnimationSync(scene, syncConfig);
    this.initializeSystem();
  }

  // 初始化动画系统
  private initializeSystem(): void {
    console.log('🎮 PhaserAnimationManager: 初始化Phaser3原生动画系统');
  }

  // 预加载史莱姆动画资源 (依赖BattleScene的资源加载，无需重复加载)
  preloadFrameAssets(): void {
    console.log('🎮 PhaserAnimationManager: 使用BattleScene预加载的史莱姆资源');
    // 注意：资源已由BattleScene.preload()方法加载，此处无需重复加载
  }

  // Load slime animation assets (基于demo中的正确实现)
  private loadSlimeAnimationAssets(character: 'green'): void {
    const characterConfig = CHARACTER_CONFIG[character];
    const basePath = `${ASSET_CONFIG.CHARACTER_BASE_PATH}/${character}`;

    Object.entries(ANIMATION_CONFIG).forEach(([animType, config]) => {
      this.loadAnimationFrames(character, animType as AnimationType, config, basePath, characterConfig.colorId);
    });

    // 也加载武器动画资源
    this.loadWeaponAssets(characterConfig.weaponType);
  }

  // Extract frame loading logic (基于demo实现)
  private loadAnimationFrames(
    character: 'green', 
    animType: AnimationType, 
    config: AnimationConfig, 
    basePath: string,
    colorId: string
  ): void {
    for (let i = 0; i < config.frames; i++) {
      const frameStr = this.getFrameString(animType, i);
      // Legacy note: underscore key format (e.g., red_idle_0). Runtime统一使用 Loader 键名 char-<num>-<anim>-<frame>
      const textureKey = `${character}_${animType}_${i}`;
      const imagePath = `${basePath}/${ASSET_CONFIG.SLIME_FILE_PREFIX}-${colorId}-${animType}_${frameStr}${ASSET_CONFIG.FILE_EXTENSION}`;
      
      this.scene.load.image(textureKey, imagePath);
      this.preloadedAssets.add(textureKey);
      
      // console.log(`加载史莱姆纹理: ${textureKey} -> ${imagePath}`); // 注释掉史莱姆纹理加载日志
    }
  }

  // Generate texture key with consistent format (matching BattleScene format)
  private generateTextureKey(character: 'green', animType: AnimationType, frameIndex: number): string {
    return `${character}_${animType}_${frameIndex}`;
  }

  // 加载武器动画资源 (基于demo实现)
  private loadWeaponAssets(weaponType: string): void {
    const weaponConfig = {
      sword: { path: `${ASSET_CONFIG.WEAPON_BASE_PATH}/sword/`, prefix: '像素史莱姆-wep1-atk_', frames: 8 },
      axe: { path: `${ASSET_CONFIG.WEAPON_BASE_PATH}/axe/`, prefix: '像素史莱姆-wep2-atk_', frames: 8 },
      lance: { path: `${ASSET_CONFIG.WEAPON_BASE_PATH}/lance/`, prefix: '像素史莱姆-wep3-atk_', frames: 8 }
    };

    const config = weaponConfig[weaponType as keyof typeof weaponConfig];
    if (!config) {
      console.warn(`未知武器类型: ${weaponType}`);
      return;
    }

    for (let i = 0; i < config.frames; i++) {
      const textureKey = `weapon-${weaponType}-${i}`;
      const imagePath = `${config.path}${config.prefix}${i}.png`;
      this.scene.load.image(textureKey, imagePath);
      this.preloadedAssets.add(textureKey);
      
      // console.log(`加载武器纹理: ${textureKey} -> ${imagePath}`); // 注释掉武器纹理加载日志
    }
  }

  // Frame string formatting with improved clarity
  private getFrameString(animType: AnimationType, frameIndex: number): string {
    const DOUBLE_DIGIT_ANIMATIONS: ReadonlySet<AnimationType> = new Set(['idle', 'rush', 'use', 'die']);
    return DOUBLE_DIGIT_ANIMATIONS.has(animType) ?
      frameIndex.toString().padStart(2, '0') :
      frameIndex.toString();
  }

  // 创建Phaser3原生动画定义（改为使用 SpriteAssetLoader 键名：char-<num>-<anim>-<frame>）
  private createPhaserAnimation(character: string, animType: AnimationType): void {
    const animKey = `${character}_${animType}`; // 播放用key仍保持兼容（color_anim）
    if (this.scene.anims.exists(animKey)) return;

    const config = ANIMATION_CONFIG[animType];
    const charType = colorToCharacterType(character.toLowerCase()) as unknown as CharacterType;
    // 验证至少一帧存在
    for (let i = 0; i < config.frames; i++) {
      if (!isCharacterFrameLoaded(this.scene.textures, charType, animType as unknown as CoreAnimType, i)) {
        const missingKey = getCharacterFrameKey(charType, animType as unknown as CoreAnimType, i);
        console.warn(`⚠️ 动画帧缺失，跳过创建动画 '${animKey}': 缺少纹理 '${missingKey}'`);
        return;
      }
    }
    const frames: { key: string; frame: any }[] = [];
    for (let i = 0; i < config.frames; i++) {
      frames.push({ key: getCharacterFrameKey(charType, animType as unknown as CoreAnimType, i), frame: null });
    }
    this.scene.anims.create({ key: animKey, frames, frameRate: config.frameRate, repeat: config.repeat });
  }

  // 验证必要的史莱姆纹理是否已加载（按指定颜色）
  private verifySlimeTexturesLoaded(color: string): boolean {
    // 优先按 Loader 规范检测：char-<num>-idle-00
    try {
      const ct = colorToCharacterType(color.toLowerCase()) as unknown as CharacterType;
      if (isCharacterFrameLoaded(this.scene.textures, ct, CoreAnimType.IDLE, 0)) return true;
      if (isCharacterFrameLoaded(this.scene.textures, ct, CoreAnimType.IDLE, 1)) return true;
    } catch {}

    // 打印可用的 Loader 颜色（反查已有 char-<num>-idle-*）
    try {
      const loadedTextureKeys = Object.keys(this.scene.textures.list);
      const nums = new Set<number>();
      loadedTextureKeys.forEach(k => {
        const m = k.match(/^char-(\d+)-idle-/);
        if (m) nums.add(parseInt(m[1], 10));
      });
      const availableColors = Array.from(nums).map(n => ({ 0:'green',1:'red',2:'purple',3:'blue',4:'white',5:'black',6:'orange' } as any)[n]).filter(Boolean);
      console.warn(`⚠️ 颜色 '${color}' 的idle纹理未就绪。可用颜色: ${availableColors.join(', ')}`);
    } catch {}
    return false;
  }

  // Create agent animation with improved structure and constants
  createAgentAnimation(agentId: string, x: number, y: number, color: string): any {
    // 验证目标颜色纹理是否已加载
    if (!this.verifySlimeTexturesLoaded(color)) {
      throw new Error('史莱姆纹理尚未加载完成，无法创建Agent动画');
    }

    const container = this.createAgentContainer(x, y);
    const characterSprite = this.createCharacterSprite(color); // 🔧 传递颜色参数
    
    container.add(characterSprite);
    this.storeAgentReferences(agentId, container, characterSprite, color); // 🔧 传递颜色
    this.initializeAgentAnimation(agentId);
    
    // console.log(`创建Agent: ${agentId} at (${x}, ${y})`); // 注释掉Agent创建日志
    
    return { 
      x: container.x, 
      y: container.y, 
      setPosition: (x: number, y: number) => container.setPosition(x, y) 
    };
  }

  // Create container positioned at tile center coordinates (基于demo实现)
  private createAgentContainer(x: number, y: number): Phaser.GameObjects.Container {
    // 放置在瓦片中心而不是左上角（动态读取当前场景的 tileSize/gridSize）
    const tileSize = ((): number => {
      const s: any = this.scene as any;
      const byProp = s?.gridSize;
      const byReg = (this.scene.registry && (this.scene.registry as any).get)
        ? ((this.scene.registry as any).get('gridSize') || (this.scene.registry as any).get('tileSize'))
        : undefined;
      return (typeof byProp === 'number' && byProp > 0)
        ? byProp
        : (typeof byReg === 'number' && byReg > 0 ? byReg : ASSET_CONFIG.TILE_SIZE);
    })();
    const container = this.scene.add.container(
      x * tileSize + tileSize / 2,
      y * tileSize + tileSize / 2
    );
    container.setDepth(1500); // 设置容器深度，确保史莱姆在地图上层
    // console.log(`📦 史莱姆容器深度设置: ${container.depth} at (${x}, ${y})`); // 注释掉容器深度设置日志
    return container;
  }

  // Create character sprite with proper setup (使用BattleScene一致的纹理key格式)
  private createCharacterSprite(color: string): Phaser.GameObjects.Sprite {
    // 优先使用 Loader 风格首帧键
    let firstFrameKey: string | null = null;
    try {
      const ct = colorToCharacterType(color.toLowerCase()) as unknown as CharacterType;
      const tryFrames = [0,1];
      for (const i of tryFrames) {
        const k = getCharacterFrameKey(ct, CoreAnimType.IDLE, i);
        if (this.scene.textures.exists(k)) { firstFrameKey = k; break; }
      }
    } catch {}
    if (!firstFrameKey) {
      throw new Error(`❌ 找不到颜色 '${color}' 的idle首帧纹理，请确认资源是否已通过 SpriteAssetLoader 预载`);
    }
    // console.log(`🎨 使用史莱姆纹理: ${firstFrameKey} (颜色: ${color})`); // 注释掉纹理使用日志

    // 最终验证：确保纹理在Phaser中存在
    if (!this.scene.textures.exists(firstFrameKey)) {
      throw new Error(`❌ 纹理键 '${firstFrameKey}' 在Phaser纹理管理器中不存在！`);
    }

    const sprite = this.scene.add.sprite(0, 0, firstFrameKey);
    // 恢复既有显示比例（保证与既有UI/视觉一致）
    sprite.setScale(ASSET_CONFIG.SPRITE_SCALE);
    sprite.setDepth(1500); // 关键：设置史莱姆在地图上层显示
    // 居中校准：再向左校准2px → (-8, -2)
    const CENTER_OFFSET = { x: -8, y: -2 } as const;
    sprite.setPosition(CENTER_OFFSET.x, CENTER_OFFSET.y);
    // console.log(`🎨 史莱姆精灵深度设置: ${sprite.depth}`); // 注释掉精灵深度设置日志

    // 🔧 确保sprite的anims对象被正确初始化
    if (!sprite.anims) {
      console.warn('⚠️ Sprite.anims未自动初始化，尝试手动创建');
      // Phaser的Sprite应该自动有anims，如果没有说明有严重问题
    } else {
      // console.log(`✅ Sprite.anims已初始化，可用方法: ${Object.getOwnPropertyNames(sprite.anims).join(', ')}`); // 注释掉动画初始化日志
    }

    // 统一该Sprite动画时间刻度，避免被外部修改导致播放速度不一致
    try { (sprite.anims as any).timeScale = 1; } catch {}

    return sprite;
  }

  // Store agent references in maps
  private storeAgentReferences(
    agentId: string,
    container: Phaser.GameObjects.Container,
    sprite: Phaser.GameObjects.Sprite,
    color: string = 'green'
  ): void {
    this.agentContainers.set(agentId, container);
    this.agentSprites.set(agentId, sprite);
    this.agentColors.set(agentId, color); // 🔧 存储agent的颜色
  }

  // Initialize agent animation (只创建动画定义，不播放)
  private initializeAgentAnimation(agentId: string): void {
    this.createPhaserAnimation('green', 'idle');
    // 移除自动播放idle，由SinglePathAnimationEngine统一控制

    // 🔧 为Theater模式预创建所有7种颜色的动画，避免战斗时缺失
    const allColors = ['green', 'red', 'purple', 'blue', 'white', 'black', 'orange'] as const;
    const allAnimTypes = ['idle', 'atk', 'hurt', 'die', 'rush', 'use', 'mov'] as const;

    // 创建所有颜色和动画类型的组合（使用 Loader 键名验证与生成）
    allColors.forEach(color => {
      allAnimTypes.forEach(animType => {
        this.createPhaserAnimation(color, animType);
      });
    });
  }

  // 播放动画 (使用Phaser3原生sprite.anims.play)
  playAnimation(agentId: string, animationType: AnimationType): boolean {
    // 若已持武器，优先尝试分层动画（与测试保持一致）；失败则显式恢复基底可见
    try {
      const ok = this.playLayeredIfEquipped(agentId, animationType);
      if (ok) return true;
      this.ensureBaseVisible(agentId);
    } catch { this.ensureBaseVisible(agentId); }
    const sprite = this.agentSprites.get(agentId);
    if (!sprite) {
      console.error(`Agent sprite not found: ${agentId}`);
      return false;
    }

    // 创建动画定义 (如果不存在)，使用agent的实际颜色
    const agentColor = this.agentColors.get(agentId) || 'green';
    this.createPhaserAnimation(agentColor, animationType);
    
    const animKey = `${agentColor}_${animationType}`; // 🔧 使用agent的实际颜色
    
    if (!this.scene.anims.exists(animKey)) {
      console.warn(`⚠️ 动画不存在: ${animKey}，回退播放 ${agentColor}_idle`);
      this.createPhaserAnimation(agentColor, 'idle');
      const idleKey = `${agentColor}_idle`;
      if (this.scene.anims.exists(idleKey)) {
        try { sprite.anims.play(idleKey); } catch {}
      }
      return false;
    }
    
    // 使用Phaser3原生动画播放API
    try { sprite.anims.play(animKey); } catch (e) {
      console.warn(`播放动画失败: ${animKey}`, e);
      return false;
    }
    
    // console.log(`播放动画: ${agentId} -> ${animKey}`); // 注释掉动画播放日志
    return true;
  }

  // 带朝向的动画播放 (使用sprite.setFlipX)
  playAnimationWithDirection(agentId: string, animationType: AnimationType, direction: Direction): boolean {
    const sprite = this.agentSprites.get(agentId);
    if (!sprite) return false;

    // 设置朝向
    sprite.setFlipX(direction === 'left');
    
    return this.playAnimation(agentId, animationType);
  }

  // 动画连接播放
  chainAnimations(agentId: string, animations: AnimationType[]): boolean {
    if (animations.length === 0) return false;
    
    // 播放第一个动画
    const firstAnim = animations[0];
    this.playAnimation(agentId, firstAnim);
    
    // 设置动画完成后播放下一个
    if (animations.length > 1) {
      const sprite = this.agentSprites.get(agentId);
      if (sprite) {
        sprite.anims.once('animationcomplete', () => {
          this.chainAnimations(agentId, animations.slice(1));
        });
      }
    }
    
    return true;
  }

  // Update agent position with improved error handling (保持向后兼容)
  updateAgentPosition(agentId: string, x: number, y: number): void {
    const container = this.agentContainers.get(agentId);
    if (!container) {
      console.warn(`Agent container not found: ${agentId}`);
      return;
    }
    // 更新到tile中心位置（动态 tileSize）
    const tileSize = ((): number => {
      const s: any = this.scene as any;
      const byProp = s?.gridSize;
      const byReg = (this.scene.registry && (this.scene.registry as any).get)
        ? ((this.scene.registry as any).get('gridSize') || (this.scene.registry as any).get('tileSize'))
        : undefined;
      return (typeof byProp === 'number' && byProp > 0)
        ? byProp
        : (typeof byReg === 'number' && byReg > 0 ? byReg : ASSET_CONFIG.TILE_SIZE);
    })();
    container.setPosition(
      x * tileSize + tileSize / 2,
      y * tileSize + tileSize / 2
    );
    // 同时更新MovementSync的位置记录
    this.movementSync.updateCurrentPosition(agentId, x, y);
  }

  // 🆕 新增：带动画的同步移动方法 (基于SlimeMovementDemo的成功模式)
  moveAgentWithAnimation(
    agentId: string,
    targetTileX: number,
    targetTileY: number,
    onComplete?: () => void
  ): boolean {
    const sprite = this.agentSprites.get(agentId);
    const container = this.agentContainers.get(agentId);
    if (!sprite || !container) {
      console.error(`Agent sprite or container not found: ${agentId}`);
      return false;
    }

    // 若已持武器，优先播放分层移动动画（循环），并照常移动容器；失败则恢复基底
    try {
      const ok = this.playLayeredIfEquipped(agentId, 'mov');
      if (!ok) this.ensureBaseVisible(agentId);
    } catch { this.ensureBaseVisible(agentId); }
    // 确保基础动画存在，使用agent的实际颜色（作为兜底）
    const agentColor = this.agentColors.get(agentId) || 'green';
    // 统一使用 'mov' 作为移动动画，避免_walk_/_mov_纹理键不一致
    this.createPhaserAnimation(agentColor, 'mov');
    this.createPhaserAnimation(agentColor, 'idle');

    // 🔧 修复：使用容器移动而不是精灵移动，确保HP条、名字等同步
    const tileSize = ((): number => {
      const s: any = this.scene as any;
      const byProp = s?.gridSize;
      const byReg = (this.scene.registry && (this.scene.registry as any).get)
        ? ((this.scene.registry as any).get('gridSize') || (this.scene.registry as any).get('tileSize'))
        : undefined;
      return (typeof byProp === 'number' && byProp > 0)
        ? byProp
        : (typeof byReg === 'number' && byReg > 0 ? byReg : ASSET_CONFIG.TILE_SIZE);
    })();
    return this.movementSync.performContainerMove(
      container,  // 移动整个容器
      sprite,     // 精灵用于动画控制
      agentId,
      targetTileX,
      targetTileY,
      tileSize,
      `${agentColor}_mov`,  // 移动动画key，统一为mov
      `${agentColor}_idle`, // idle动画key，使用agent的实际颜色
      () => {
        // 移动完成后同步内部状态
        this.movementSync.updateCurrentPosition(agentId, targetTileX, targetTileY);
        // 若为持武器分层动画，移动完成后强制回到分层 IDLE，避免“原地走路”
        try {
          if (this.equippedTypes.has(agentId)) {
            const okIdle = this.playLayeredIfEquipped(agentId, 'idle');
            if (!okIdle) this.ensureBaseVisible(agentId);
          }
        } catch { this.ensureBaseVisible(agentId); }
        if (onComplete) onComplete();
        console.log(`✅ Agent ${agentId} 容器移动完成 to (${targetTileX}, ${targetTileY})`);
      }
    );
  }

  // ==== 分层动画桥接：装备武器的Agent自动切换为“持武器”系列 ====
  private playLayeredIfEquipped(agentId: string, animationType: AnimationType): boolean {
    // 前置条件检查：若不满足，直接返回，不改动基底可见性
    const w = this.equippedTypes.get(agentId);
    if (!w) return false;
    const player = (this.scene as any).inWorldAnimationPlayer;
    if (!player || typeof player.playLayeredAnimation !== 'function') return false;
    const container = this.agentContainers.get(agentId);
    if (!container) return false;

    // 颜色/武器/动画映射
    const color = (this.agentColors.get(agentId) || 'green').toLowerCase();
    const charType: LayerCharType = this.mapColorToCharacterType(color);
    const weapType: LayerWeaponType = this.mapWeaponStringToLayerWeapon(w);
    const anim: LayerAnim = this.mapAnimToLayer(animationType);
    const loop = (anim === LayerAnim.IDLE || anim === LayerAnim.MOVE);

    // 隐藏基础精灵（避免重影），若后续失败，确保恢复
    const baseSprite = this.agentSprites.get(agentId);
    try {
      if (baseSprite) { baseSprite.setVisible(false); }
      player.playLayeredAnimation(
        agentId,
        charType,
        weapType,
        anim,
        baseSprite ? { x: baseSprite.x, y: baseSprite.y } : { x: 0, y: 0 },
        {
          loop,
          fps: anim === LayerAnim.MOVE ? 12 : 15,
          parentContainer: container,
          scale: 1.3,
          onComplete: () => {
            // 结束类动画回退到分层idle；保持基底隐藏以避免双影
            if (!loop) {
              try { player.playLayeredAnimation(agentId, charType, weapType, LayerAnim.IDLE, { x:0, y:0 }, { loop: true, fps: 10, parentContainer: container, scale: 1.3 }); } catch {}
            }
          }
        }
      );
      return true;
    } catch (e) {
      console.warn('playLayeredIfEquipped failed:', (e as any)?.message || e);
      // 兜底：分层播放失败时，立即恢复基础精灵可见
      try { if (baseSprite) baseSprite.setVisible(true); } catch {}
      return false;
    }
  }

  // 小工具：确保基底精灵可见（防守式恢复）
  private ensureBaseVisible(agentId: string): void {
    const sp = this.agentSprites.get(agentId);
    if (sp && !sp.visible) {
      try { sp.setVisible(true); } catch {}
    }
  }

  private mapColorToCharacterType(color: string): LayerCharType {
    switch (color) {
      case 'green': return LayerCharType.GREEN;
      case 'red': return LayerCharType.RED;
      case 'purple': return LayerCharType.PURPLE;
      case 'blue': return LayerCharType.BLUE;
      case 'white': return LayerCharType.WHITE;
      case 'black': return LayerCharType.BLACK;
      case 'orange': return LayerCharType.ORANGE;
      default: return LayerCharType.GREEN;
    }
  }

  private mapWeaponStringToLayerWeapon(w: string): LayerWeaponType {
    const t = String(w).toUpperCase();
    if (t === 'AXE') return LayerWeaponType.AXE;
    if (t === 'LANCE') return LayerWeaponType.LANCE;
    return LayerWeaponType.SWORD;
  }

  private mapAnimToLayer(anim: AnimationType): LayerAnim {
    // 统一映射：'walk' 也视作 'mov'
    const a = (anim === 'walk') ? 'mov' : anim;
    switch (a) {
      case 'idle': return LayerAnim.IDLE;
      case 'mov': return LayerAnim.MOVE;
      case 'atk': return LayerAnim.ATTACK;
      case 'hurt': return LayerAnim.HURT;
      case 'die': return LayerAnim.DIE;
      case 'rush': return LayerAnim.RUSH;
      case 'use': return LayerAnim.USE;
      default: return LayerAnim.IDLE;
    }
  }

  // 🆕 检查Agent是否正在移动
  isAgentMoving(agentId: string): boolean {
    return this.movementSync.isMoving(agentId);
  }

  // 🆕 获取Agent当前瓦片位置
  getAgentTilePosition(agentId: string): { x: number; y: number } {
    return this.movementSync.getCurrentPosition(agentId);
  }

  // Removed legacy handleGameEvent; use explicit play* methods from adapters/engine.

  // 注册动画完成回调（按代理一次性监听）
  onAnimationComplete(agentId: string, callback: () => void): void {
    const sprite = this.agentSprites.get(agentId);
    if (!sprite) {
      console.warn(`onAnimationComplete: Agent sprite not found: ${agentId}`);
      // 立即回调避免阻塞
      setTimeout(callback, 100);
      return;
    }

    // 优先使用Sprite事件，兼容性更好
    try {
      const EV = (Phaser as any).Animations?.Events?.ANIMATION_COMPLETE || 'animationcomplete';
      if (typeof (sprite as any).once === 'function') {
        (sprite as any).once(EV, () => { try { callback(); } catch {} });
      } else if (sprite.anims && typeof (sprite.anims.once) === 'function') {
        sprite.anims.once('animationcomplete', () => { try { callback(); } catch {} });
      } else {
        console.warn(`onAnimationComplete: 无可用的once监听，fallback callback for ${agentId}`);
        setTimeout(callback, 100);
      }
    } catch (e) {
      console.warn('onAnimationComplete registration failed:', e);
      setTimeout(callback, 100);
    }
  }

  // 🧹 统一移除Agent（容器/基础精灵/武器/装备状态） — 已下方提供更完整实现，避免重复声明
  // 注意：此处原有的 removeAgent 版本已删除，使用文件末尾更完整的实现（含 MovementSync 清理与日志）

  // Play weapon animation with improved error handling
  private playWeaponAnimation(agentId: string, weaponType: WeaponType): void {
    const sprite = this.agentSprites.get(agentId);
    if (!sprite) {
      console.warn(`Agent sprite not found for weapon animation: ${agentId}`);
      return;
    }
    
    const weaponAnimKey = `green-attack-${weaponType}`;
    console.log(`播放武器动画: ${weaponAnimKey}`);
    sprite.anims.play(weaponAnimKey);
  }

  // Add agent with weapon using improved structure
  addAgentWithWeapon(agentId: string, x: number, y: number, weaponType: WeaponType): any {
    const agent = this.createAgentAnimation(agentId, x, y);
    this.attachWeaponToAgent(agentId, weaponType);
    return agent;
  }

  // Attach weapon sprite to agent container
  private attachWeaponToAgent(agentId: string, weaponType: WeaponType): void {
    const container = this.agentContainers.get(agentId);
    if (!container) {
      console.warn(`Agent container not found for weapon attachment: ${agentId}`);
      return;
    }
    
    const weaponSprite = this.scene.add.sprite(0, 0, `weapon-${weaponType}-idle-0`);
    container.add(weaponSprite);
    this.weaponSprites.set(agentId, weaponSprite);
    
    console.log(`添加武器: ${agentId} -> ${weaponType}`);
  }

  // 🆕 临时方案：用地图资源图标作为手持标记，保证拾取/装备时“手上有武器”的可视化
  equipWeaponIcon(agentId: string, weaponType: string): void {
    const container = this.agentContainers.get(agentId);
    if (!container) { console.warn(`equipWeaponIcon: no container for ${agentId}`); return; }

    // 先移除旧的武器图标
    const old = this.weaponSprites.get(agentId);
    if (old) { old.destroy(); this.weaponSprites.delete(agentId); }

    const keyMap: Record<string, string> = {
      'SWORD': 'resource-sword',
      'AXE': 'resource-axe',
      'LANCE': 'resource-lance',
      'sword': 'resource-sword',
      'axe': 'resource-axe',
      'lance': 'resource-lance'
    };
    const texKey = keyMap[weaponType] || 'resource-sword';
    if (!this.scene.textures.exists(texKey)) {
      console.warn(`equipWeaponIcon: texture not found: ${texKey}`);
      return;
    }
    const sprite = this.scene.add.sprite(8, -6, texKey);
    sprite.setScale(0.7);
    sprite.setDepth(1501);
    container.add(sprite);
    this.weaponSprites.set(agentId, sprite);
    this.equippedTypes.set(agentId, weaponType);
  }

  // ==== Global equip state helpers (always show held weapon until drop) ====
  public setEquippedWeapon(agentId: string, weaponType: string): void {
    // Persist equip state and ensure icon overlay exists for all subsequent animations
    try { this.equipWeaponIcon(agentId, weaponType); } catch {}
    this.equippedTypes.set(agentId, weaponType);
  }

  public clearEquippedWeapon(agentId: string): void {
    this.equippedTypes.delete(agentId);
    const old = this.weaponSprites.get(agentId);
    if (old) { try { old.destroy(); } catch {} this.weaponSprites.delete(agentId); }
    // 恢复基础精灵显示并停止分层动画
    try { (this.scene as any).inWorldAnimationPlayer?.stopAnimation(agentId); } catch {}
    const base = this.agentSprites.get(agentId);
    if (base) { try { base.setVisible(true); } catch {} }
  }

  // Animation control methods with improved error handling
  pauseAgentAnimation(agentId: string): void {
    this.controlAgentAnimation(agentId, 'pause');
  }

  resumeAgentAnimation(agentId: string): void {
    this.controlAgentAnimation(agentId, 'resume');
  }

  stopAgentAnimation(agentId: string): void {
    this.controlAgentAnimation(agentId, 'stop');
  }

  // Unified animation control with error handling
  private controlAgentAnimation(agentId: string, action: 'pause' | 'resume' | 'stop'): void {
    const sprite = this.agentSprites.get(agentId);
    if (!sprite) {
      console.warn(`Agent sprite not found for ${action} action: ${agentId}`);
      return;
    }
    
    sprite.anims[action]();
  }

  // Removed legacy getAnimationMapping.

  // 性能和调试信息
  getAnimationDebugInfo(): any {
    return {
      activeAnimations: this.agentSprites.size,
      phaserVersion: '3.70.0', // Phaser版本
      memoryUsage: this.agentSprites.size * 1024 // 估算内存使用
    };
  }

  // Performance metrics with improved accuracy
  getAnimationPerformanceMetrics(): {
    activeSprites: number;
    frameRate: number;
    memoryEstimate: number;
  } {
    const MEMORY_PER_SPRITE = 1024;
    const ASSUMED_FPS = 60;
    
    const totalSprites = this.agentSprites.size + this.weaponSprites.size;
    
    return {
      activeSprites: totalSprites,
      frameRate: ASSUMED_FPS,
      memoryEstimate: totalSprites * MEMORY_PER_SPRITE
    };
  }

  // 添加Agent方法 (颜色参数为必填)
  addAgent(agentId: string, x: number, y: number, color: string): any {
    return this.createAgentAnimation(agentId, x, y, color);
  }

  // 播放Agent动画方法 (向后兼容)
  playAgentAnimation(agentId: string, animationType: AnimationType): boolean {
    return this.playAnimation(agentId, animationType);
  }

  // 带朝向播放动画方法 (向后兼容)
  playAgentAnimationWithDirection(agentId: string, animationType: AnimationType, direction: Direction): boolean {
    return this.playAnimationWithDirection(agentId, animationType, direction);
  }

  // 🔥 Phase A.2: Enhanced Character Loading - 支持所有7种史莱姆颜色
  preloadAllCharacterAssets(): void {
    console.log('🎮 PhaserAnimationManager: 预加载所有7种史莱姆颜色动画帧');
    
    // 配置常量 (与BattleScene保持一致)
    const CHARACTER_CONFIG = {
      colors: ['green', 'red', 'blue', 'black', 'white', 'orange', 'purple'] as const,
      animationTypes: ['idle', 'mov', 'hurt', 'rush', 'use', 'die', 'atk'] as const,
      colorIdMap: {
        'green': '0', 'red': '1', 'blue': '3', 'black': '5',
        'white': '4', 'orange': '6', 'purple': '2'
      } as const,
      frameConfig: {
        'idle': { count: 11, format: 'padded' },
        'rush': { count: 11, format: 'padded' },
        'use': { count: 11, format: 'padded' },
        'die': { count: 12, format: 'padded' },
        'mov': { count: 8, format: 'simple' },
        'atk': { count: 8, format: 'simple' },
        'hurt': { count: 3, format: 'simple' }
      } as const,
      basePath: 'assets/characters',
      filePrefix: '像素史莱姆'
    } as const;

    // 加载所有颜色的动画帧
    CHARACTER_CONFIG.colors.forEach(color => {
      CHARACTER_CONFIG.animationTypes.forEach(animationType => {
        const frameInfo = CHARACTER_CONFIG.frameConfig[animationType];
        const colorId = CHARACTER_CONFIG.colorIdMap[color] || '0';
        
        for (let i = 0; i < frameInfo.count; i++) {
          const frameFileName = this.generateChineseFrameFileName(
            colorId, animationType, i, frameInfo.format
          );
          const textureKey = `${color}_${animationType}_${i}`;
          const path = `${CHARACTER_CONFIG.basePath}/${color}/${frameFileName}`;
          this.scene.load.image(textureKey, path);
          this.preloadedAssets.add(textureKey);
        }
      });
    });
    
    // 启动加载
    this.scene.load.start();
  }

  // 🔥 根据已登记的agent颜色返回动画键（不再通过agentId猜测颜色）
  getAnimationKeyForAgent(agentId: string, animationType: string): string {
    const color = this.agentColors.get(agentId) || 'green';
    return `${color}-${animationType}`;
  }

  // 🔥 Phase A.2: 生成中文文件名路径  
  generateChineseAssetPath(color: string, animationType: string, frameIndex: number): string {
    const colorIdMap: { [key: string]: string } = {
      'green': '0', 'red': '1', 'blue': '3', 'black': '5',
      'white': '4', 'orange': '6', 'purple': '2'
    };
    const colorId = colorIdMap[color] || '0';
    const frameFileName = this.generateChineseFrameFileName(
      colorId, animationType, frameIndex, 
      ['idle', 'rush', 'use', 'die'].includes(animationType) ? 'padded' : 'simple'
    );
    return `assets/characters/${color}/${frameFileName}`;
  }

  // 为特定Agent创建动画配置（按已登记颜色）
  createAnimationForAgent(agentId: string, animationType: string): void {
    const characterColor = this.agentColors.get(agentId) || 'green';
    const animKey = `${characterColor}-${animationType}`;
    
    // 如果动画已存在，跳过
    if (this.scene.anims.exists(animKey)) {
      return;
    }

    // 获取动画配置
    const animConfig = this.getAnimationConfig(animationType);
    if (!animConfig) {
      console.warn(`未知动画类型: ${animationType}`);
      return;
    }

    // 生成帧数组
    const frames: Array<{ key: string, frame: null }> = [];
    for (let i = 0; i < animConfig.frames; i++) {
      const textureKey = `${characterColor}-${animationType}-${i}`;
      frames.push({ key: textureKey, frame: null });
    }

    // 创建Phaser3动画
    this.scene.anims.create({
      key: animKey,
      frames: frames,
      frameRate: animConfig.frameRate,
      repeat: animConfig.repeat
    });

    console.log(`为Agent创建动画: ${agentId} -> ${animKey}`);
  }

  // 私有辅助方法：生成中文帧文件名
  private generateChineseFrameFileName(
    colorId: string, 
    animationType: string, 
    frameIndex: number, 
    format: 'padded' | 'simple'
  ): string {
    const frameNum = format === 'padded' 
      ? frameIndex.toString().padStart(2, '0')
      : frameIndex.toString();
    return `像素史莱姆-${colorId}-${animationType}_${frameNum}.png`;
  }

  // 私有辅助方法：获取动画配置
  private getAnimationConfig(animationType: string) {
    // 统一帧率配置（固定不缩放），保证全局、全玩家动画速度一致
    const cfg = {
      'idle': { frames: 11, frameRate: 10, repeat: -1 },
      'walk': { frames: 8, frameRate: 16, repeat: -1 },
      'mov': { frames: 8, frameRate: 16, repeat: -1 },
      'hurt': { frames: 3, frameRate: 15, repeat: 0 },
      'die': { frames: 12, frameRate: 8, repeat: 0 },
      'rush': { frames: 11, frameRate: 20, repeat: 0 },
      'use': { frames: 11, frameRate: 10, repeat: 0 },
      'atk': { frames: 8, frameRate: 15, repeat: 0 }
    } as const;
    return cfg[animationType as keyof typeof cfg];
  }

  // 移除Agent及其视觉元素
  removeAgent(agentId: string): void {
    const container = this.agentContainers.get(agentId);
    const sprite = this.agentSprites.get(agentId);
    
    if (container) {
      container.destroy();
      this.agentContainers.delete(agentId);
    }
    
    if (sprite) {
      sprite.destroy();
      this.agentSprites.delete(agentId);
    }
    
    // 清理武器精灵
    const weaponSprite = this.weaponSprites.get(agentId);
    if (weaponSprite) {
      weaponSprite.destroy();
      this.weaponSprites.delete(agentId);
    }
    
    // 🆕 清理MovementSync状态
    this.movementSync.cleanupEntity(agentId);
    
    console.log(`🗑️ Agent ${agentId} 已从场景中移除`);
  }
}
